import axios from "axios";
import { useState } from "react";
import { FILE_RULES, REQUIRED_COLUMNS } from "../constants/validationRules";
import {
  setError,
  setValidationResults,
  setValidationStatus,
} from "../context/bulkUploadActions";
import { normalizeColumnNames, parseExcelFile } from "../utils/excelParser";
import {
  findDuplicateStudents,
  normalizePhone,
  validateStudentRow,
} from "../utils/validators";
import { useBulkUpload } from "./useBulkUpload";

export const useValidation = () => {
  const { state, dispatch } = useBulkUpload();
  const [validating, setValidating] = useState(false);

  const validateFile = async (file, programStartDate, programId) => {
    setValidating(true);
    dispatch(setValidationStatus("validating"));

    try {
      // Step 1: Parse Excel
      let parsedData = await parseExcelFile(file);
      parsedData = normalizeColumnNames(parsedData);

      // Remove empty rows
      parsedData = parsedData.filter((row) => {
        return Object.values(row).some(
          (val) => val && val.toString().trim() !== ""
        );
      });

      // Check row count
      if (parsedData.length === 0) {
        throw new Error("File is empty or contains no valid data");
      }

      if (parsedData.length > FILE_RULES.MAX_ROWS) {
        throw new Error(
          `File contains ${parsedData.length} rows. Maximum allowed is ${FILE_RULES.MAX_ROWS}`
        );
      }

      // Step 2: Check for required columns
      const missingColumns = REQUIRED_COLUMNS.filter(
        (col) => !Object.keys(parsedData[0]).includes(col)
      );

      if (missingColumns.length > 0) {
        throw new Error(
          `Missing required columns: ${missingColumns.join(", ")}`
        );
      }

      // Step 3: Validate each row
      const validationErrors = [];
      parsedData.forEach((row, index) => {
        const rowErrors = validateStudentRow(row, index + 1, programStartDate);
        if (rowErrors) {
          validationErrors.push(rowErrors);
        }
      });

      // Step 4: Check for duplicate students within file
      const duplicateStudents = findDuplicateStudents(parsedData);
      duplicateStudents.forEach((dup) => {
        validationErrors.push({
          row: dup.row,
          errors: [
            {
              field: "student",
              message: `Duplicate student (already in row ${dup.duplicateOf})`,
            },
          ],
          data: dup.data,
        });
      });

      // If there are validation errors, return them without calling backend
      if (validationErrors.length > 0) {
        dispatch(
          setValidationResults({
            results: {
              valid: [],
              errors: validationErrors,
              warnings: [],
            },
            summary: {
              totalRows: parsedData.length,
              validRows: 0,
              errorRows: validationErrors.length,
              warningRows: 0,
              newParents: 0,
              existingParents: 0,
              totalStudents: 0,
            },
          })
        );
        setValidating(false);
        return false;
      }

      // Step 5: Send to backend for parent matching
      const response = await axios.post(
        "/api/school-programs-bulk-upload/validate",
        {
          students: parsedData.map((row) => ({
            parent1: {
              firstname: row.parent1_firstname,
              lastname: row.parent1_lastname,
              email: row.parent1_email,
              phone: normalizePhone(row.parent1_phone),
            },
            parent2: row.parent2_firstname
              ? {
                  firstname: row.parent2_firstname,
                  lastname: row.parent2_lastname,
                  email: row.parent2_email,
                  phone: normalizePhone(row.parent2_phone),
                }
              : null,
            student: {
              firstname: row.student_firstname,
              lastname: row.student_lastname,
              dob: row.student_dob,
            },
            programStartDate: row.program_start_date,
          })),
          programId,
        }
      );

      const { valid, warnings, summary } = response.data;

      dispatch(
        setValidationResults({
          results: {
            valid,
            errors: [],
            warnings,
          },
          summary,
        })
      );

      setValidating(false);
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      dispatch(setError(error.message || "Validation failed"));
      dispatch(setValidationStatus("error"));
      setValidating(false);
      return false;
    }
  };

  return {
    validateFile,
    validating,
  };
};
